import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneIncoming, X, User, ExternalLink } from 'lucide-react';
import { neonRawSelect } from '../services/api';
import VipBadge from './VipBadge';

const CX_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/cxone-proxy'
  : '/.netlify/functions/cxone-proxy';

const POLL_INTERVAL = 10000; // 10 seconds

// Known company numbers to ignore
const IGNORE_NUMBERS = new Set(['4109683167', '8773282722']);

function formatPhone(num) {
  if (!num) return '';
  const digits = String(num).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return num;
}

export default function ScreenPop({ navigateTo, enabled = true }) {
  const [activeCall, setActiveCall] = useState(null); // { contactId, phone, agent, customer, dismissed }
  const [dismissed, setDismissed] = useState(new Set());
  const seenContacts = useRef(new Set());
  const customerCache = useRef({});

  const lookupCustomer = useCallback(async (phone) => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (!digits || digits.length < 7 || IGNORE_NUMBERS.has(digits)) return null;
    if (customerCache.current[digits]) return customerCache.current[digits];

    try {
      const res = await neonRawSelect(
        `SELECT c.contactid, c.firstname, c.lastname, c.email, c.phone, c.city, c.state,
                (SELECT COUNT(*) FROM orders WHERE ocustomerid = c.contactid) as order_count,
                (SELECT COALESCE(SUM(CASE WHEN orderamount ~ '^[0-9.-]+$' THEN orderamount::numeric ELSE 0 END), 0) FROM orders WHERE ocustomerid = c.contactid) as lifetime_value,
                CASE WHEN EXISTS (SELECT 1 FROM vip_memberships v WHERE v.customer_id = c.contactid::int AND v.active = true) THEN true ELSE false END as is_vip,
                CASE WHEN EXISTS (SELECT 1 FROM vip_memberships v WHERE v.customer_id = c.contactid::int AND v.active = true AND v.auto_renew = false) THEN true ELSE false END as vip_not_renewing,
                EXISTS(SELECT 1 FROM celebrity_candidates cc WHERE cc.contactid = c.contactid AND cc.review_action = 'confirmed') as is_celeb,
                (SELECT STRING_AGG(cc.name || ' (' || COALESCE(cc.category, 'other') || ')', ', ') FROM celebrity_candidates cc WHERE cc.contactid = c.contactid AND cc.review_action = 'confirmed') as celeb_names
         FROM customers c
         WHERE c.phone LIKE '%${digits}%' OR c.shipphone LIKE '%${digits}%'
         LIMIT 1`
      );
      if (res.rows?.length > 0) {
        const r = res.rows[0];
        const customer = {
          id: r.contactid,
          name: [r.firstname, r.lastname].filter(Boolean).join(' ') || 'Unknown',
          email: r.email,
          phone: r.phone,
          location: [r.city, r.state].filter(Boolean).join(', '),
          orderCount: Number(r.order_count || 0),
          lifetimeValue: Number(r.lifetime_value || 0),
          isVip: !!r.is_vip,
          vipNotRenewing: !!r.vip_not_renewing,
          isCeleb: !!r.is_celeb,
          celebNames: r.celeb_names,
        };
        customerCache.current[digits] = customer;
        return customer;
      }
    } catch (e) { /* lookup failed, not critical */ }
    return null;
  }, []);

  useEffect(() => {
    let timer;
    let mounted = true;

    const poll = async () => {
      try {
        const res = await fetch(CX_PROXY, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'active-contacts' }),
        });
        const data = await res.json();
        const contacts = data.activeContacts || [];

        for (const contact of contacts) {
          const cid = contact.contactId;
          if (!cid || seenContacts.current.has(cid) || dismissed.has(cid)) continue;
          seenContacts.current.add(cid);

          // Determine caller phone (inbound = fromAddress, outbound = toAddress)
          const phone = contact.isOutbound ? contact.toAddress : contact.fromAddress;
          if (!phone) continue;

          const digits = phone.replace(/\D/g, '').slice(-10);
          if (IGNORE_NUMBERS.has(digits)) continue;

          // Only pop on inbound calls
          if (contact.isOutbound) continue;

          // Look up customer
          const customer = await lookupCustomer(phone);
          const agent = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unknown Agent';

          if (mounted) {
            setActiveCall({
              contactId: cid,
              phone,
              agent,
              direction: contact.isOutbound ? 'outbound' : 'inbound',
              skill: contact.skillName || '',
              customer,
            });
          }
        }

        // Auto-dismiss when the active call is no longer in the active contacts list
        if (mounted) {
          setActiveCall(prev => {
            if (!prev) return null;
            const stillActive = contacts.some(c => c.contactId === prev.contactId);
            if (!stillActive) return null;
            return prev;
          });
        }
      } catch (e) { /* poll failed, retry next interval */ }

      if (mounted) timer = setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => { mounted = false; clearTimeout(timer); };
  }, [dismissed, lookupCustomer]);

  const handleDismiss = () => {
    if (activeCall) {
      setDismissed(prev => new Set([...prev, activeCall.contactId]));
      setActiveCall(null);
    }
  };

  const handleViewAccount = () => {
    if (activeCall?.customer?.email) {
      navigateTo?.('customer-search', null, { email: activeCall.customer.email });
      handleDismiss();
    }
  };

  // Check if screen pop is disabled via user preference (synced from Neon)
  if (!enabled) return null;
  if (!activeCall) return null;

  const { phone, agent, direction, skill, customer } = activeCall;
  const fmt = n => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 10000,
      width: 340, borderRadius: 12,
      background: '#1e293b', color: '#fff',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      overflow: 'hidden',
      animation: 'slideUp 0.3s ease-out',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: direction === 'inbound' ? '#166534' : '#1e40af',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PhoneIncoming size={16} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{direction === 'inbound' ? 'Incoming Call' : 'Outgoing Call'}</div>
            <div style={{ fontSize: 10, opacity: 0.8 }}>{agent}{skill ? ` — ${skill}` : ''}</div>
          </div>
        </div>
        <button onClick={handleDismiss} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 2, opacity: 0.7 }}>
          <X size={16} />
        </button>
      </div>

      {/* Phone number */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.5 }}>
          <Phone size={14} style={{ marginRight: 6, opacity: 0.5 }} />
          {formatPhone(phone)}
        </div>
      </div>

      {/* Customer info */}
      {customer ? (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
              {customer.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {customer.name}
                {customer.isVip && <VipBadge notRenewing={customer.vipNotRenewing} style={{ fontSize: 9, fontWeight: 700, background: 'var(--orange)', color: '#fff', padding: '2px 6px', borderRadius: 3, marginLeft: 6, verticalAlign: 'middle' }} />}
                {customer.isCeleb && <span title={customer.celebNames || 'Confirmed celebrity'} style={{ fontSize: 9, fontWeight: 700, background: '#8b5cf6', color: '#fff', padding: '2px 6px', borderRadius: 3, marginLeft: 4, verticalAlign: 'middle', cursor: 'help' }}>CELEB</span>}
              </div>
              {customer.isCeleb && customer.celebNames && (
                <div style={{ fontSize: 10, color: '#c4b5fd', fontWeight: 500, marginTop: 1 }}>🌟 {customer.celebNames}</div>
              )}
              <div style={{ fontSize: 10, opacity: 0.6 }}>{customer.email}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 10px' }}>
              <div style={{ fontSize: 9, opacity: 0.5, textTransform: 'uppercase' }}>Orders</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{customer.orderCount}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 10px' }}>
              <div style={{ fontSize: 9, opacity: 0.5, textTransform: 'uppercase' }}>Lifetime Value</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#4ade80' }}>{fmt(customer.lifetimeValue)}</div>
            </div>
          </div>

          {customer.location && (
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 10 }}>{customer.location}</div>
          )}

          <button onClick={handleViewAccount} style={{
            width: '100%', padding: '8px 0', borderRadius: 6, border: 'none',
            background: 'var(--orange)', color: '#fff', fontWeight: 700, fontSize: 12,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <ExternalLink size={13} /> View Customer Account
          </button>
        </div>
      ) : (
        <div style={{ padding: '14px', textAlign: 'center' }}>
          <User size={20} style={{ opacity: 0.3, marginBottom: 6 }} />
          <div style={{ fontSize: 12, opacity: 0.5 }}>No matching customer found</div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
